import { skipToken, useQuery } from '@tanstack/react-query'
import { conciergeFireService, firerouterService, tenantService } from '@chilipiper/service'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { SlugMappingResponse } from '@chilipiper/api-type-def'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { endpoint } from '@chilipiper/config'
import { getDomain, parseQuery } from '@chilipiper/utils'
import { useEffect } from 'react'
import { captureException, setTags } from '@chilipiper/sentry'
import { RouteLocation } from '../types'

const useGetTenantIdFromSubdomain = () => {
  const domain = getDomain()
  const query = useQuery({
    queryKey: ['migration', 'get-tenant-id', domain],
    queryFn: () => {
      return tenantService.getTenantIdFromSubdomain(domain)
    },
    refetchOnWindowFocus: false,
    retry: 2,
  })

  useEffect(() => {
    if (query.error) {
      captureException(query.error as any)
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const useFireToggle = () => {
  const domain = getDomain()
  const { data: tenantId } = useGetTenantIdFromSubdomain()
  const query = useQuery({
    queryKey: ['migration', 'use-fire', 'toggle', domain],
    queryFn: () => conciergeFireService.getFireToggle(tenantId as string),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    retry: 2,
  })

  useEffect(() => {
    if (query.error) {
      captureException(query.error as any)
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const isBookingLink = (pathname: string, params: URLSearchParams) =>
  pathname.startsWith('/book') && !params.has('rescheduleId') && !pathname.includes('/dialer/')
const isPersonalBookingLink = (queueSlug: string) => queueSlug === 'me'
const isRouterLink = (pathname: string) => pathname.startsWith('/router')

const handleMappingRedirect = ({
  slugResponse,
  typeResponse,
  byResponse,
  additionalSearchParams,
  isPersonalWorkspace,
}: {
  slugResponse?: SlugMappingResponse | undefined
  typeResponse?: SlugMappingResponse | undefined
  byResponse?: SlugMappingResponse | undefined
  additionalSearchParams?: URLSearchParams | undefined
  isPersonalWorkspace?: boolean | undefined
}) => {
  const searchParams = new URLSearchParams(additionalSearchParams)
  if (!slugResponse?.fireSlug) {
    return Promise.resolve('')
  }

  const domain = getDomain()
  const devBaseUrl = endpoint.apiRoot().replace('api', domain).replace('.na', '')
  const prodBaseUrl = window.location.origin.replace('.na', '')
  const baseUrl = window.location.origin.includes('.team') ? devBaseUrl : prodBaseUrl
  let redirectUrl = ''
  if (byResponse?.fireSlug) {
    searchParams.set('by', byResponse.fireSlug)
  }
  switch (slugResponse.entityType) {
    case 'router':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      redirectUrl = `${baseUrl}/concierge-router/link/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'user':
      redirectUrl = `${baseUrl}/${
        isPersonalWorkspace || !typeResponse ? 'me' : 'shared'
      }/${slugResponse.fireSlug}${typeResponse ? `/${typeResponse.fireSlug}` : ''}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'queue':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      redirectUrl = `${baseUrl}/round-robin/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'ownershipSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      redirectUrl = `${baseUrl}/ownership/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'groupSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      redirectUrl = `${baseUrl}/group/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    default:
      captureException(new Error('Unknown entity type'), scope => {
        scope.setContext('Unknown entity type', { entityType: slugResponse.entityType, domain })
        return scope
      })
      break
  }
  return Promise.resolve(redirectUrl)
}

const useFormId = () => {
  const location = useLocation() as RouteLocation
  const routeMatch = useRouteMatch()
  const { meetingTypeSlugMapping, isLoadingMeetingTypeSlugMapping } = useMeetingType()
  const domain = getDomain()

  const { data, isPending } = useQuery({
    queryKey: [
      'migration',
      'formId',
      'mapping',
      domain,
      location.pathname,
      meetingTypeSlugMapping?.legacySlug || 'no-type',
    ],
    queryFn: () => {
      if (!location.pathname) {
        return Promise.resolve({})
      }
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete('type')
      searchParams.delete('domain')
      if (meetingTypeSlugMapping?.legacySlug) {
        return firerouterService
          .mapping(domain, 'meeting-type-form', meetingTypeSlugMapping.legacySlug)
          .then(({ fireSlug }) => fireSlug)
      }
      if (isRouterLink(location.pathname)) {
        const [routerName] = Object.values<string>(routeMatch.params)
        return firerouterService
          .mapping(domain, 'router-form', routerName)
          .then(({ fireSlug }) => fireSlug)
      }
      if (
        isBookingLink(location.pathname, searchParams) &&
        !isPersonalBookingLink(location.pathname)
      ) {
        const [path] = Object.values<string>(routeMatch.params)
        const [queueSlug] = path.split('/')
        return firerouterService
          .mapping(domain, 'queue-default-meeting-type-form', queueSlug)
          .then(({ fireSlug }) => fireSlug)
      }
      return Promise.resolve({})
    },
    enabled: !isLoadingMeetingTypeSlugMapping,
    retry: 2,
  })
  return {
    formId: data,
    isLoadingFormId: isPending,
  }
}

const useMeetingType = () => {
  const query = parseQuery()
  const type = query.type
  const domain = getDomain()

  const { data, isPending } = useQuery({
    queryKey: ['migration', 'meeting-type', 'mapping', domain, type || 'no-type'],
    queryFn: type
      ? () => {
          // type can be either a slug or an id
          // so we need to check both in order to get the correct mapping
          return Promise.all([
            firerouterService.mapping(domain, 'adminSchedulingLink', type),
            firerouterService.mappingByLegacyId(domain, 'adminSchedulingLink', type),
          ]).then(([meetingTypeBySlugResponse, meetingTypeByIdResponse]) => {
            if (!meetingTypeBySlugResponse.entityFireId) {
              return meetingTypeByIdResponse
            }
            return meetingTypeBySlugResponse
          })
        }
      : skipToken,
    retry: 2,
  })
  return {
    meetingTypeSlugMapping: data,
    isLoadingMeetingTypeSlugMapping: Boolean(type) && isPending,
  }
}

export const useFireRedirect = () => {
  const location = useLocation() as RouteLocation
  const routeMatch = useRouteMatch()
  const query = parseQuery()
  const domain = getDomain()
  const type = query.type
  const { formId, isLoadingFormId } = useFormId()
  const { meetingTypeSlugMapping, isLoadingMeetingTypeSlugMapping } = useMeetingType()
  const { data: useAllProductsFire, isPending: isLoadingFireToggle } = useFireToggle()
  const tags: Record<string, string> = {
    domain,
  }

  const { isPending: isLoadingRedirect, error } = useQuery({
    queryKey: [
      'migration',
      'redirect',
      domain,
      location.pathname,
      window.location.search,
      type || 'no-type',
      query.by || 'no-by',
      query.bookId || 'no-book-id',
      formId || '',
      meetingTypeSlugMapping?.entityLegacyId || '',
    ],
    queryFn: async () => {
      if (!location.pathname) {
        return Promise.resolve({})
      }
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete('type')
      searchParams.delete('domain')
      let slugPromise: Promise<SlugMappingResponse | undefined> = Promise.resolve(undefined)
      let bookerFromBookIdPromise: SlugMappingResponse | undefined = undefined
      if (query.bookId) {
        tags.bookId = query.bookId
        bookerFromBookIdPromise = await firerouterService.mappingByLegacyId(
          domain,
          'suggestedTimesBooker',
          query.bookId
        )
        // if the bookId is present in the url but there is no mapping for it, we need to stop redirecting
        if (bookerFromBookIdPromise.fireSlug === query.bookId) {
          return Promise.resolve({})
        }
      }
      if (isBookingLink(location.pathname, searchParams)) {
        const [path] = Object.values<string>(routeMatch.params)
        const [queueSlug, userSlug] = path.split('/')
        tags.queueSlug = queueSlug
        tags.userSlug = userSlug
        if (isPersonalBookingLink(queueSlug)) {
          tags.isPersonalBookingLink = 'true'
          slugPromise = firerouterService.mapping(domain, 'user', userSlug)
        } else {
          const { fireSlug } = await firerouterService.mapping(domain, 'user', userSlug)
          if (fireSlug) {
            searchParams.set('assignee', fireSlug)
          }
          const { fireSlug: queueAlgorithm } = await firerouterService.mapping(
            domain,
            'queue-algorithm',
            queueSlug
          )
          if (queueAlgorithm === 'Group') {
            slugPromise = firerouterService.mapping(domain, 'groupSchedulingLink', queueSlug)
          } else if (queueAlgorithm === 'Ownership') {
            slugPromise = firerouterService.mapping(domain, 'ownershipSchedulingLink', queueSlug)
          } else {
            slugPromise = firerouterService.mapping(domain, 'queue', queueSlug)
          }
        }
      } else if (isRouterLink(location.pathname)) {
        const [routerName] = Object.values<string>(routeMatch.params)
        tags.routerName = routerName
        slugPromise = firerouterService.mapping(domain, 'router', routerName)
      } else {
        return Promise.resolve({})
      }
      const promises: Promise<SlugMappingResponse | undefined>[] = [slugPromise]
      const booker = (() => {
        if (query.by) {
          tags.by = query.by
          return firerouterService.mapping(domain, 'user', query.by)
        }
        if (bookerFromBookIdPromise) {
          return Promise.resolve(bookerFromBookIdPromise)
        }
        return Promise.resolve(undefined)
      })()
      promises.push(booker)
      promises.push(
        meetingTypeSlugMapping?.legacySlug
          ? firerouterService.mapping(
              domain,
              'meeting-type-type',
              meetingTypeSlugMapping.legacySlug
            )
          : Promise.resolve(undefined)
      )
      if (formId) {
        Array.from(searchParams.entries()).forEach(([key]) => {
          promises.push(
            firerouterService.mappingByLegacyId(domain, 'form.block.mapping', `${formId}.${key}`)
          )
        })
      }
      setTags(tags)
      return Promise.all(promises).then(
        ([slugResponse, byResponse, meetingTypeTypeResponse, ...blockResponses]) => {
          const isPersonalWorkspace =
            !meetingTypeTypeResponse || meetingTypeTypeResponse.fireSlug === 'Personal'
          const newSearchParams = new URLSearchParams(searchParams)
          blockResponses.forEach(block => {
            if (
              block?.entityFireId &&
              block.entityLegacyId &&
              block?.entityFireId !== block.entityLegacyId
            ) {
              const formFieldName = block.entityLegacyId.replace(`${formId}.`, '')
              newSearchParams.set(block.entityFireId, searchParams.get(formFieldName) || '')
              if (!formFieldName.includes('utm_')) {
                newSearchParams.delete(formFieldName)
              }
            }
          })
          return handleMappingRedirect({
            slugResponse,
            typeResponse: meetingTypeSlugMapping,
            byResponse,
            additionalSearchParams: newSearchParams,
            isPersonalWorkspace,
          }).then(redirectUrl => {
            window.location.href = redirectUrl
            return redirectUrl
          })
        }
      )
    },
    enabled: Boolean(useAllProductsFire) && !isLoadingFormId && !isLoadingMeetingTypeSlugMapping,
    retry: 2,
  })

  useEffect(() => {
    if (error) {
      defaultErrorCallback(error as any)
      window.location.href = `${window.location.origin}/book/not-available`
    }
  }, [error])

  return {
    data: useAllProductsFire,
    isLoading: isLoadingFireToggle || (Boolean(useAllProductsFire) && isLoadingRedirect),
  }
}
